import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import _ from 'lodash';

import { Col, Row, Tooltip } from 'grab-ui';
import scribe from '@grab-x/scribe-web-sdk';
import ScribeEventName from 'omega-rtc/constants/scribe';
import { MetricsPerformance } from 'omega-rtc/types/ucd/campaign';
import { SCORE_CATEGORY, SCORE_COLOR } from 'omega-rtc/constants/metrics';
import 'views/trident-campaigns/common/TridentCampaignView.scss';
import FAQDrawer from 'omega-rtc/views/ucd/FAQDrawer';

import type { StoreState } from 'reducers';
import type { UserState } from 'reducers/user';

import checkedIcon from 'img/checked_icon.svg';
import thumbsUpIcon from 'img/thumbs-up.svg';
import warnIcon from 'img/warn_icon.svg';
import infoIcon from 'img/information_icon.svg';


import './TridentTemplateCard.scss';
import './PerformancePanel.scss';


type Props = {
  metricsPerformanceList: Array<MetricsPerformance>, // [{channel: "Push", rateName: "Open Rate", upliftPercentage: 10.3}]
  scoreCategory: string, // Possible values: Great, Good, Bad
  numOfTopMetrics: number, // number of top x metrics shown on the panel.
  user: UserState,
};


export class PerformancePanel extends Component {
  props: Props;

  constructor(props) {
    super(props);
    this.state = {
      drawerVisible: false,
    };
  }

  getTitle = (scoreCategory: string, metricsLength: number) => {
    const metricsName = `${metricsLength} ${metricsLength === 1 ? 'metric is' : 'metrics are'}`;
    let icon;
    let text;

    switch (scoreCategory) {
      case SCORE_CATEGORY.great:
        icon = <img src={thumbsUpIcon} alt="thumbs-up" />;
        text = `${metricsName} awesome`;
        break;
      case SCORE_CATEGORY.good:
        icon = <img src={checkedIcon} alt="check" />;
        text = `${metricsName} performing well`;
        break;
      default:
        icon = <img src={warnIcon} alt="exclamation" />;
        text = `${metricsLength} metric${metricsLength !== 1 ? 's' : ''} need improvement`;
        break;
    }

    return (
      <h4 className="metrics-card-title" style={{ color: SCORE_COLOR[scoreCategory] }}>
        <span>{icon}</span> {text}
      </h4>
    );
  };

  onOpenDrawer = () => {
    this.setState({
      drawerVisible: true,
    });
    // send scribe event.
    scribe.sendEvent(ScribeEventName.CAMPAIGN_METRICS, {
      type: 'faq_drawer_opened',
      email: _.get(this.props, 'user.email'),
    });
  }

  onCloseDrawer = () => {
    this.setState({
      drawerVisible: false,
    });
  }

  getFooterText = (scoreCategory: string, metricsLength: number) => {
    const metricsName = `${metricsLength} metric${metricsLength !== 1 ? 's' : ''}`;

    switch (scoreCategory) {
      case SCORE_CATEGORY.great:
        return `Top ${metricsName} in this level. Try to replicate the same success.`;
      case SCORE_CATEGORY.good:
        return `Top ${metricsName} which fall behind the 90th percentile. Try to reach higher goals.`;
      default:
        return `Top ${metricsName} which fall behind the average. Start the optimisation from here.`;
    }
  };

  render() {
    const { scoreCategory, numOfTopMetrics } = this.props;
    const metricsList = _.get(this.props, 'metricsPerformanceList', []);
    const topSortedMetricsList = _.take(
      metricsList.sort((a, b) => Math.abs(b.upliftPercentage) - Math.abs(a.upliftPercentage)),
      numOfTopMetrics);
    const columnWidth = numOfTopMetrics === 1 ? 24 : 12;

    return (
      <Fragment>
        <div className={'metrics-card-container'}>
          <Tooltip onClick={this.onOpenDrawer}>
            <div className={'tooltip-holder'}>
              <img src={infoIcon} alt="information-icon" />
            </div>
          </Tooltip>
          {this.getTitle(scoreCategory, metricsList.length)}
          <Row className={'metrics-card-detail'}>
            {_.map(topSortedMetricsList, metrics => {
              const rateName = _.get(metrics, 'rateName');
              const channel = _.get(metrics, 'channel');
              const percentage = _.get(metrics, 'upliftPercentage', 0);

              return (<Col key={rateName + channel} span={columnWidth} className={'metrics-card-detail-col'}>
                <span className={'arrow'}>
                  {percentage > 0 ? '▲' : '▼'}
                </span>
                <h5>
                  {Math.abs(percentage.toFixed(2))}pp</h5>
                <p>{rateName} of {channel}</p>
              </Col>);
            })
            }
          </Row>
          <div className={'metrics-card-footer'}>
            <p>{this.getFooterText(scoreCategory, Math.min(numOfTopMetrics, topSortedMetricsList.length))}</p>
          </div>
        </div>
        <FAQDrawer
          onClose={this.onCloseDrawer}
          visible={this.state.drawerVisible}
        />
      </Fragment>
    );
  }
}

function mapStateToProps(state: StoreState) {
  return {
    user: state.app.user,
  };
}

export default connect(mapStateToProps, {
})(PerformancePanel);
